/**
 * Remote sessions (visio tutoring, RI, RA, field events).
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from '@/services/api-client';
import { toLocalDateString } from '@/utils/format-date-utils';
import type {
  Session,
  CreateSessionDto,
  UpdateSessionDto,
  SessionWithParticipants,
  SessionParticipant,
  SessionStatus,
  RecurrencePreview,
  RecurrencePreviewInput,
} from '@/types/inperson/session.types';
import type {
  ListItem,
  SessionsResponse,
  QueryParams,
  CancelSessionDto,
  AddParticipantDto,
  SessionLiveKitPresenceResponse,
} from '@/types/remote/session.types';

/** Skip empty query values when building remote session list URLs. */
function appendQueryValue(
  params: URLSearchParams,
  key: string,
  value: string | number | undefined | null,
): void {
  if (value === undefined || value === null || value === '') return;
  params.append(key, String(value));
}

/** Query string for `GET /sessions` list and `GET /sessions/export`. */
const buildSessionQueryParams = (params?: QueryParams): URLSearchParams => {
  const queryParams = new URLSearchParams();
  appendQueryValue(queryParams, 'page', params?.page);
  appendQueryValue(queryParams, 'pageSize', params?.pageSize);
  params?.types?.forEach((type) => queryParams.append('types', type));
  params?.statuses?.forEach((status) => queryParams.append('statuses', status.toUpperCase()));
  appendQueryValue(queryParams, 'sessionFormat', params?.sessionFormat);
  if (params?.dateFrom) queryParams.append('dateFrom', toLocalDateString(params.dateFrom));
  if (params?.dateTo) queryParams.append('dateTo', toLocalDateString(params.dateTo));
  appendQueryValue(queryParams, 'search', params?.search);
  appendQueryValue(queryParams, 'sortBy', params?.sortBy);
  appendQueryValue(queryParams, 'sortOrder', params?.sortOrder);
  appendQueryValue(queryParams, 'tutorUserId', params?.tutorUserId);
  appendQueryValue(queryParams, 'studentUserId', params?.studentUserId);
  params?.managedByIds?.forEach((id) => queryParams.append('managedByIds', id));
  appendQueryValue(queryParams, 'animatorUserId', params?.animatorUserId);
  appendQueryValue(queryParams, 'programId', params?.programId);
  if (params?.justificationFilter && params.justificationFilter !== 'all') {
    queryParams.append('justificationFilter', params.justificationFilter);
  }
  appendQueryValue(queryParams, 'schoolYearId', params?.schoolYearId);
  appendQueryValue(queryParams, 'recurrenceType', params?.recurrenceType);
  return queryParams;
};

/** `GET /sessions` — paginated remote sessions. */
export const getSessions = async (params?: QueryParams): Promise<SessionsResponse> => {
  const queryParams = buildSessionQueryParams(params);
  const queryString = queryParams.toString();
  const url = queryString ? `/sessions?${queryString}` : '/sessions';

  const response = await apiClient.get<{
    data: Array<Omit<ListItem, 'sessionDate'> & { sessionDate: string; status: string }>;
    total: number;
    page: number;
    pageSize: number;
  }>(url);

  return {
    data: response.data.data.map((session) => {
      const [year, month, day] = session.sessionDate.split('-').map(Number);
      const sessionDate = new Date(year, month - 1, day);

      return {
        ...session,
        sessionDate,
        status: session.status as SessionStatus,
      };
    }),
    total: response.data.total,
    page: response.data.page,
    pageSize: response.data.pageSize,
  };
};

/** `GET /sessions/:id` plus participants. */
export const getSession = async (id: string): Promise<SessionWithParticipants> => {
  const [sessionRes, participantsRes] = await Promise.all([
    apiClient.get<Omit<Session, 'sessionDate'> & { sessionDate: string }>(`/sessions/${id}`),
    apiClient.get<SessionParticipant[]>(`/sessions/${id}/participants`),
  ]);

  const [year, month, day] = sessionRes.data.sessionDate.split('-').map(Number);
  const sessionDate = new Date(year, month - 1, day);

  return {
    ...sessionRes.data,
    sessionDate,
    participants: participantsRes.data,
  };
};

/** `POST /sessions` */
export const createSession = async (dto: CreateSessionDto): Promise<Session | Session[]> => {
  const { data } = await apiClient.post('/sessions', dto);
  return data;
};

/** `PATCH /sessions/:id` */
export const updateSession = async (id: string, dto: UpdateSessionDto): Promise<Session> => {
  const { data } = await apiClient.patch(`/sessions/${id}`, dto);
  return data;
};

/** `POST /sessions/:id/cancel` */
export const cancelSession = async (id: string, dto: CancelSessionDto): Promise<Session> => {
  const { data } = await apiClient.post<Session>(`/sessions/${id}/cancel`, dto);
  return data;
};

/** `POST /sessions/:sessionId/participants` */
export const addSessionParticipant = async (
  sessionId: string,
  dto: AddParticipantDto,
): Promise<SessionParticipant> => {
  const { data } = await apiClient.post<SessionParticipant>(
    `/sessions/${sessionId}/participants`,
    dto,
  );
  return data;
};

/** `DELETE /sessions/:sessionId/participants/:participantId` */
export const deleteSessionParticipant = async (
  sessionId: string,
  participantId: string,
): Promise<void> => {
  await apiClient.delete(`/sessions/${sessionId}/participants/${participantId}`);
};

/** `GET /sessions/:sessionId/livekit/presence` */
export const getSessionLiveKitPresence = async (
  sessionId: string,
): Promise<SessionLiveKitPresenceResponse> => {
  const { data } = await apiClient.get<SessionLiveKitPresenceResponse>(
    `/sessions/${sessionId}/livekit/presence`,
  );
  return data;
};

/** Trigger a browser download for an XLSX blob. */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /sessions/export` — download XLSX. */
export const exportSessionsExcel = async (
  params?: Omit<QueryParams, 'page' | 'pageSize'>,
): Promise<void> => {
  const queryParams = buildSessionQueryParams({
    ...params,
    sessionFormat: 'remote',
  });

  const response = await apiClient.get(`/sessions/export?${queryParams.toString()}`, {
    responseType: 'blob',
  });
  triggerBlobDownload(
    new Blob([response.data], { type: XLSX_MIME }),
    `sessions-remote-${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
};

/** `POST /sessions/recurrence-preview` — count and planning end for a meeting series. */
export const previewRecurrence = async (
  input: RecurrencePreviewInput,
): Promise<RecurrencePreview> => {
  const { data } = await apiClient.post<RecurrencePreview>('/sessions/recurrence-preview', input);
  return data;
};

/**
 * Generate a guest meeting token for admin/coordinator to join RI/RA meeting
 */
/** `POST /sessions/:sessionId/guest-meeting-token` */
export async function getGuestMeetingToken(
  sessionId: string,
): Promise<{ guestToken: string; expiresAt: string }> {
  const response = await apiClient.post(`/sessions/${sessionId}/guest-meeting-token`);
  return response.data;
}
